/**
 * Project Scope schemas — ADR-0033 (Phase 1).
 *
 * A project is a LENS over the tenant: anything that runs inside it sees
 * only the project's bound sources (Phase 1) and, later, its bound MCP
 * servers / API connections (Phase 2) and workflows (Phase 4). These Zod
 * schemas are the single source of truth (CLAUDE.md Rule #26) for the
 * binding rows, the create payloads, the strict scope-narrowing shape and
 * the response envelopes the /ai-projects/:id/* endpoints and the project
 * page share.
 *
 * Tables (migration c16_project_scope.sql):
 *   project_sources       — what a project can SEE (folder | connector_repo | document)
 *   project_mcp_bindings  — what a project can CALL (whole MCP server; tool_allowlist reserved)
 *   project_api_bindings  — what a project can CALL (API connection)
 */

import { z } from 'zod';

// ─── Source kinds + refs ────────────────────────────────────────────────────

export const ProjectSourceKindSchema = z.enum(['folder', 'connector_repo', 'document']);
export type ProjectSourceKind = z.infer<typeof ProjectSourceKindSchema>;

/**
 * `connectorId:owner/repo`. The connector id is a UUID (36 chars, hex +
 * dashes). owner/repo may contain `_`, `.` or `-` but never `:` or `/`
 * beyond the single separator — the resolver splits on the FIRST `:` and
 * matches `library_connector_docs.external_source_path` with the prefix
 * `github:{owner}/{repo}:` (the adapter's materialisation format).
 *
 * CASE (PSP1-12, ADR-0033 §8): this schema validates SHAPE, not case, and
 * deliberately stays case-preserving — GitHub owners and repository names
 * are case-preserving too, and the stored `external_source_path` carries
 * the connector's configured spelling verbatim (connectors/dedup.ts's
 * `safe()` never lower-cases). Case is normalised at WRITE instead:
 * `validateSourceRef` returns the connector-configured spelling as
 * `canonicalRef` and POST /ai-projects/:id/sources persists THAT, not the
 * caller's verbatim string. The resolver additionally matches
 * case-insensitively as defence in depth, because a connector's config can
 * be edited after a binding is made.
 */
export const ConnectorRepoRefSchema = z
  .string()
  .regex(
    /^[0-9a-f-]{36}:[^\s/:]+\/[^\s/:]+$/,
    'connector_repo ref must be "connectorId:owner/repo"',
  );
export type ConnectorRepoRef = z.infer<typeof ConnectorRepoRefSchema>;

/** Split a validated connector_repo ref into its parts. */
export function parseConnectorRepoRef(ref: string): { connectorId: string; ownerRepo: string } {
  const idx = ref.indexOf(':');
  return { connectorId: ref.slice(0, idx), ownerRepo: ref.slice(idx + 1) };
}

/**
 * `library_connectors.config.selectedRepos` → `owner/repo` strings.
 *
 * PSP1-02 — the GitHub adapter's ConfigSchema stores OBJECTS
 * (`selectedRepos: z.array(z.object({ owner, name }))`, adapters/github.ts)
 * and the create route persists the adapter's validated output verbatim, so
 * every real connector holds `[{ owner: 'viaanix', name: 'argusai-2' }]`.
 * Matching those entries as strings rejected every possible connector_repo
 * binding (400 INVALID_SOURCE_REF) and made the picker list no repos at all.
 *
 * The plain-string form is still accepted: older/hand-written configs and
 * the shape used by non-GitHub adapters.
 *
 * Returns lower-cased-comparable values as written (case is preserved; the
 * callers compare case-insensitively) and skips anything malformed.
 */
export function normalizeSelectedRepos(selectedRepos: unknown): string[] {
  if (!Array.isArray(selectedRepos)) return [];
  const out: string[] = [];
  for (const entry of selectedRepos) {
    if (typeof entry === 'string') {
      if (entry.includes('/')) out.push(entry);
      continue;
    }
    if (entry && typeof entry === 'object') {
      const { owner, name } = entry as { owner?: unknown; name?: unknown };
      if (typeof owner === 'string' && typeof name === 'string' && owner && name) {
        out.push(`${owner}/${name}`);
      }
    }
  }
  return out;
}

// ─── Create payload (discriminated on sourceKind) ───────────────────────────

export const ProjectSourceCreateSchema = z.discriminatedUnion('sourceKind', [
  z.object({ sourceKind: z.literal('folder'), sourceRef: z.string().uuid() }),
  z.object({ sourceKind: z.literal('document'), sourceRef: z.string().uuid() }),
  z.object({ sourceKind: z.literal('connector_repo'), sourceRef: ConnectorRepoRefSchema }),
]);
export type ProjectSourceCreate = z.infer<typeof ProjectSourceCreateSchema>;

// ─── Tool-binding create payloads (Phase 2, T14) ────────────────────────────

/**
 * Bind a whole MCP server (design decision 3 — never per tool; `tool_allowlist`
 * stays nullable and reserved). The ref is the TENANT'S install id, not the
 * catalog server id: a project binds what its tenant installed, and the install
 * is what carries the endpoint + vaulted credential.
 */
export const ProjectMcpBindCreateSchema = z.object({
  tenantMcpInstallId: z.string().uuid(),
});
export type ProjectMcpBindCreate = z.infer<typeof ProjectMcpBindCreateSchema>;

/** Bind an API connection (`tenant_api_connections.id`). */
export const ProjectApiBindCreateSchema = z.object({
  tenantApiConnectionId: z.string().uuid(),
});
export type ProjectApiBindCreate = z.infer<typeof ProjectApiBindCreateSchema>;

// ─── Row DTOs ───────────────────────────────────────────────────────────────

export const ProjectSourceSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  organizationId: z.string().uuid(),
  sourceKind: ProjectSourceKindSchema,
  sourceRef: z.string(),
  /**
   * Human label: folder name, `owner/repo`, or document title. EMPTY when
   * `restricted` — a bound document's title is a person's view of that
   * document, read through THE Document visibility resolver for the person
   * asking, never through the store (SEC-LB4 Finding A); a bound folder's name
   * through the resolver's folder-name rule.
   */
  label: z.string(),
  /**
   * True for a `document` source the person asking may not see, or a
   * `folder` source above their clearance (founder 2026-10-22) — or one that
   * no longer exists: the two are ONE answer, so the flag is not an existence
   * oracle. Its title / name is withheld (`label` is empty).
   */
  restricted: z.boolean(),
  /** Live count of readable (non-deleted) documents the source resolves to. */
  documentCount: z.number().int().min(0),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type ProjectSource = z.infer<typeof ProjectSourceSchema>;

export const ProjectMcpBindingSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  tenantMcpInstallId: z.string().uuid(),
  /** null = whole server (design decision 3). Reserved for Phase 2. */
  toolAllowlist: z.array(z.string()).nullable(),
  createdAt: z.string(),
});
export type ProjectMcpBinding = z.infer<typeof ProjectMcpBindingSchema>;

export const ProjectApiBindingSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  tenantApiConnectionId: z.string().uuid(),
  createdAt: z.string(),
});
export type ProjectApiBinding = z.infer<typeof ProjectApiBindingSchema>;

// ─── Scope narrowing (workflow_definitions.scope_narrowing) ────────────────

/**
 * A strict SUBSET declaration of the project's bindings a workflow may
 * narrow to. `.strict()` rejects unknown keys so a typo cannot silently
 * widen. The "never wider than the project" rule is enforced in code at
 * save AND run time in Phase 4; the SHAPE is fixed now so the column can
 * land in c16 with its type.
 */
export const ScopeNarrowingSchema = z
  .object({
    sources: z.array(z.string()).optional(),
    mcp: z.array(z.string().uuid()).optional(),
    api: z.array(z.string().uuid()).optional(),
  })
  .strict();
export type ScopeNarrowing = z.infer<typeof ScopeNarrowingSchema>;

// ─── Response envelopes (shared by /ai-projects/:id/* and the web page) ────

export const ProjectSourcesResponseSchema = z.object({
  sources: z.array(ProjectSourceSchema),
  totalDocuments: z.number().int().min(0),
});
// NOTE (Foundations 1 / B0-T1): `scopeEnforced` is gone. It reported the
// tenant's `project_scope` feature flag, and there are no feature flags — the
// project lens is a rail-internal capability that is always on
// (decisions.md 2026-09-08). It was never anything but `true` after c22, and a
// badge that can never render is a lie waiting to happen.
export type ProjectSourcesResponse = z.infer<typeof ProjectSourcesResponseSchema>;

/**
 * The rendered rows the Tools tab shows — the binding plus the joined display
 * name and live count. Named (Phase 2, T14) so the bind endpoints can answer
 * with exactly the row the list renders, rather than a second shape the UI has
 * to reconcile.
 */
export const ProjectMcpBindingRowSchema = ProjectMcpBindingSchema.extend({
  displayName: z.string(),
  toolCount: z.number().int().min(0),
});
export type ProjectMcpBindingRow = z.infer<typeof ProjectMcpBindingRowSchema>;

export const ProjectApiBindingRowSchema = ProjectApiBindingSchema.extend({
  displayName: z.string(),
  endpointCount: z.number().int().min(0),
});
export type ProjectApiBindingRow = z.infer<typeof ProjectApiBindingRowSchema>;

export const ProjectToolsResponseSchema = z.object({
  mcp: z.array(ProjectMcpBindingRowSchema),
  api: z.array(ProjectApiBindingRowSchema),
});
export type ProjectToolsResponse = z.infer<typeof ProjectToolsResponseSchema>;

export const ProjectWorkflowsResponseSchema = z.object({
  workflows: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      status: z.string(),
      /** 'narrowed' when workflow_definitions.scope_narrowing IS NOT NULL. */
      mode: z.enum(['inherited', 'narrowed']),
      lastRunAt: z.string().nullable(),
    }),
  ),
});
export type ProjectWorkflowsResponse = z.infer<typeof ProjectWorkflowsResponseSchema>;

export const ProjectAgentsResponseSchema = z.object({
  agents: z.array(
    z.object({
      agentId: z.string(),
      displayName: z.string().nullable(),
      lastRunAt: z.string().nullable(),
      runCount: z.number().int().min(0),
    }),
  ),
});
export type ProjectAgentsResponse = z.infer<typeof ProjectAgentsResponseSchema>;
